import * as THREE from 'three';
import sceneFrag from './scene.frag.glsl?raw';
import quadVert from './quad.vert.glsl?raw';

export interface OrbitState {
  /** azimuth around the hole, radians */
  az: number;
  /** elevation above the disk plane, radians */
  el: number;
  /** distance from the centre in M units */
  dist: number;
  /** pitch of the view away from the hole, radians (0 looks straight at it) */
  tilt: number;
}

/**
 * Act 5's world: a full-screen quad running the Schwarzschild ray tracer.
 * Camera is described by orbit angles and distance; the sky is a texture when one loads.
 */
export class BlackHoleScene {
  readonly scene = new THREE.Scene();
  readonly camera = new THREE.Camera();
  readonly material: THREE.ShaderMaterial;
  readonly orbit: OrbitState = { az: 0.3, el: 0.12, dist: 30, tilt: 0 };
  private skyLoaded = false;

  constructor() {
    this.material = new THREE.ShaderMaterial({
      uniforms: {
        res: { value: new THREE.Vector2(1, 1) },
        time: { value: 0 },
        camPos: { value: new THREE.Vector3() },
        camRot: { value: new THREE.Matrix3() },
        fovScale: { value: 2 * Math.tan(0.5 * 0.82) },
        lift: { value: 0 },
        steps: { value: 460 },
        diskGain: { value: 1.0 },
        useSkyTex: { value: 0 },
        skyTex: { value: null },
        skyYaw: { value: 0 }
      },
      vertexShader: quadVert,
      fragmentShader: sceneFrag,
      depthTest: false,
      depthWrite: false
    });
    const quad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), this.material);
    quad.frustumCulled = false;
    this.scene.add(quad);
  }

  /** Try to load an equirectangular sky; stays procedural if the file is missing. */
  loadSky(url: string) {
    new THREE.TextureLoader().load(url, (tex) => {
      tex.colorSpace = THREE.SRGBColorSpace;
      tex.wrapS = THREE.RepeatWrapping;
      tex.minFilter = THREE.LinearMipmapLinearFilter;
      tex.anisotropy = 8;
      this.material.uniforms.skyTex.value = tex;
      this.material.uniforms.useSkyTex.value = 1;
      this.skyLoaded = true;
    }, undefined, () => { this.skyLoaded = false; });
  }

  get hasSkyTexture() { return this.skyLoaded; }

  setQuality(cinematic: boolean) {
    this.material.uniforms.steps.value = cinematic ? 460 : 220;
  }

  /** Vertical field of view in radians; the act widens it as the probe closes in. */
  setFov(rad: number) {
    this.material.uniforms.fovScale.value = 2 * Math.tan(0.5 * rad);
  }

  /** Update uniforms for this frame from the orbit state. */
  update(t: number, width: number, height: number) {
    const o = this.orbit;
    const cp = new THREE.Vector3(o.dist * Math.cos(o.el) * Math.cos(o.az), o.dist * Math.cos(o.el) * Math.sin(o.az), o.dist * Math.sin(o.el));
    const f0 = cp.clone().negate().normalize();
    const up = new THREE.Vector3(0, 0, 1);
    const r = new THREE.Vector3().crossVectors(f0, up).normalize();
    // pitch the view up, away from the hole, by the tilt
    const f = f0.clone().applyAxisAngle(r, o.tilt).normalize();
    const u = new THREE.Vector3().crossVectors(r, f);
    const m = new THREE.Matrix3();
    m.set(r.x, u.x, -f.x, r.y, u.y, -f.y, r.z, u.z, -f.z);
    this.material.uniforms.camPos.value.copy(cp);
    this.material.uniforms.camRot.value.copy(m);
    this.material.uniforms.time.value = t;
    this.material.uniforms.res.value.set(width, height);
    this.material.uniforms.lift.value = height > width * 1.05 ? 0.16 : 0;
    this.material.uniforms.skyYaw.value = t * 0.002;
  }
}
